// src/components/app/proyectos/sensorica/PhaseCard.tsx
'use client';

import { Card, CardContent, CardMedia, Typography, Box, Chip, Collapse, IconButton } from '@mui/material';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Phase } from '@/data/proyectos/sensorica/sensoricaData';

interface PhaseCardProps {
  phase: Phase;
  index: number;
}

export default function PhaseCard({ phase, index }: PhaseCardProps) {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: 6,
        },
        borderTop: `4px solid ${phase.color}`,
      }}
    >
      {phase.image && (
        <CardMedia
          component="div"
          sx={{
            height: 200,
            background: `linear-gradient(135deg, ${phase.color}20 0%, ${phase.color}40 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '4rem',
          }}
        >
          {phase.icon}
        </CardMedia>
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="overline"
            sx={{
              color: phase.color,
              fontWeight: 700,
              letterSpacing: 1.2,
            }}
          >
            {phase.title}
          </Typography>
          <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
            {phase.subtitle}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {phase.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            Ver detalles
          </Typography>
          <IconButton
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="mostrar más"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Actividades principales:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
              {phase.details.map((detail, idx) => (
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  key={idx}
                  sx={{ mb: 0.5 }}
                >
                  {detail}
                </Typography>
              ))}
            </Box>

            {phase.technologies && phase.technologies.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Tecnologías:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {phase.technologies.map((tech, idx) => (
                    <Chip
                      key={idx}
                      label={tech}
                      size="small"
                      sx={{
                        bgcolor: `${phase.color}15`,
                        color: phase.color,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}